import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, formatPrice, siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";

const getCategoryDetails = (category) => {
  if (typeof category === "string") {
    return { label: category, value: category };
  }

  return {
    label: category?.label || category?.name || category?.title || "",
    value:
      category?.value ||
      category?.slug ||
      category?.name ||
      category?.label ||
      "",
  };
};

function Header() {
  const { cartItems = [], wishlistItems = [] } = useContext(CartContext) || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef(null);
  const menuCloseRef = useRef(null);
  const menuDrawerRef = useRef(null);
  const searchButtonRef = useRef(null);
  const searchInputRef = useRef(null);

  const brandName = siteConfig.brandName || siteConfig.name || "";
  const logoSource =
    typeof siteConfig.logo === "string"
      ? siteConfig.logo
      : siteConfig.logo?.src;
  const logoAlt = siteConfig.logo?.alt || brandName;
  const announcement =
    typeof siteConfig.announcement === "string"
      ? { text: siteConfig.announcement, link: "/shop" }
      : siteConfig.announcement || {};
  const announcementText =
    announcement.text ||
    siteConfig.announcementText ||
    siteConfig.shippingMessage ||
    `Tracked shipping ${formatPrice(siteConfig.shipping.standardRate)}`;
  const announcementLink = announcement.link || "/shop";
  const categoryItems = Array.isArray(categories)
    ? categories
        .map(getCategoryDetails)
        .filter(
          (item) => item.label && item.label.trim().toLowerCase() !== "all"
        )
    : [];
  const activeCategory =
    new URLSearchParams(location.search).get("category") || "";

  const cartCount = Array.isArray(cartItems)
    ? cartItems.reduce(
        (total, item) => total + Math.max(1, Number(item.quantity) || 1),
        0
      )
    : 0;
  const wishlistCount = Array.isArray(wishlistItems) ? wishlistItems.length : 0;
  const itemLabel = (count) => `${count} ${count === 1 ? "item" : "items"}`;

  useEffect(() => {
    try {
      if (
        window.sessionStorage.getItem(siteConfig.storage.announcementKey) ===
        "true"
      ) {
        setAnnouncementVisible(false);
      }
    } catch (error) {
      // Storage may be unavailable in privacy-focused browser modes.
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);

    const query = new URLSearchParams(location.search).get("q");
    setSearchQuery(query || "");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocusedElement = document.activeElement;
    document.body.style.overflow = "hidden";
    menuCloseRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = menuDrawerRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements?.length) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!searchOpen) return undefined;

    const previouslyFocusedElement = document.activeElement;
    searchInputRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSearchOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [searchOpen]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeAnnouncement = () => {
    setAnnouncementVisible(false);
    try {
      window.sessionStorage.setItem(siteConfig.storage.announcementKey, "true");
    } catch (error) {
      // The close state still works for the current render without storage.
    }
  };

  const toggleMenu = () => {
    setSearchOpen(false);
    setMenuOpen((current) => !current);
  };

  const toggleSearch = () => {
    setMenuOpen(false);
    setSearchOpen((current) => !current);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    navigate(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
    setSearchOpen(false);
    toTop();
  };

  return (
    <header
      className={`site-header${scrolled ? " site-header--scrolled" : ""}${
        location.pathname === "/" ? " site-header--home" : ""
      }`}
    >
      {announcementVisible && announcementText && (
        <div
          className="site-header__announcement"
          role="region"
          aria-label="Announcement"
        >
          <Link
            className="site-header__announcement-link"
            to={announcementLink}
            onClick={toTop}
          >
            {announcementText}
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M4 10h11M11 6l4 4-4 4" />
            </svg>
          </Link>
          <button
            className="site-header__announcement-close"
            type="button"
            onClick={closeAnnouncement}
            aria-label="Dismiss announcement"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="m5 5 10 10M15 5 5 15" />
            </svg>
          </button>
        </div>
      )}

      <div className="site-header__surface">
        <div className="site-header__main">
          <div className="site-header__leading">
            <button
              ref={menuButtonRef}
              className="site-header__icon-button site-header__menu-button"
              type="button"
              onClick={toggleMenu}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                {menuOpen ? (
                  <path d="m5 5 14 14M19 5 5 19" />
                ) : (
                  <path d="M3 7h18M3 12h18M3 17h18" />
                )}
              </svg>
            </button>
            {siteConfig.tagline && (
              <span className="site-header__tagline">{siteConfig.tagline}</span>
            )}
          </div>

          <Link
            className="site-header__logo"
            to="/"
            onClick={toTop}
            aria-label={`${brandName} home`}
          >
            {logoSource ? (
              <img src={logoSource} alt={logoAlt} />
            ) : (
              <span>{brandName}</span>
            )}
          </Link>

          <div className="site-header__utilities" aria-label="Store tools">
            <button
              ref={searchButtonRef}
              className="site-header__utility"
              type="button"
              onClick={toggleSearch}
              aria-expanded={searchOpen}
              aria-controls="site-search"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="10.8" cy="10.8" r="6.8" />
                <path d="m16 16 4.3 4.3" />
              </svg>
              <span className="site-header__utility-label">Search</span>
            </button>

            <Link
              className="site-header__utility site-header__wishlist-link"
              to="/shop?view=wishlist"
              onClick={toTop}
              aria-label={`Wishlist, ${itemLabel(wishlistCount)}`}
            >
              <span className="site-header__icon-wrap">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.7 5.7a5 5 0 0 0-7.1 0L12 7.3l-1.6-1.6a5 5 0 0 0-7.1 7.1L12 21l8.7-8.2a5 5 0 0 0 0-7.1Z" />
                </svg>
                {wishlistCount > 0 && (
                  <span className="site-header__count">{wishlistCount}</span>
                )}
              </span>
              <span className="site-header__utility-label">Saved</span>
            </Link>

            <Link
              className="site-header__utility"
              to="/cartpage"
              onClick={toTop}
              aria-label={`Shopping bag, ${itemLabel(cartCount)}`}
            >
              <span className="site-header__icon-wrap">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 8.5h14l-1 12H6l-1-12Z" />
                  <path d="M9 9V6a3 3 0 0 1 6 0v3" />
                </svg>
                {cartCount > 0 && (
                  <span className="site-header__count">{cartCount}</span>
                )}
              </span>
              <span className="site-header__utility-label">Bag</span>
            </Link>
          </div>
        </div>

        <nav className="site-header__desktop-nav" aria-label="Main navigation">
          <Link
            className={
              location.pathname === "/shop" && !activeCategory
                ? "is-active"
                : ""
            }
            to="/shop"
            onClick={toTop}
          >
            Shop all
          </Link>
          {categoryItems.map((category) => (
            <Link
              className={activeCategory === category.value ? "is-active" : ""}
              key={category.value}
              to={`/shop?category=${encodeURIComponent(category.value)}`}
              onClick={toTop}
            >
              {category.label}
            </Link>
          ))}
          <Link
            className={location.pathname === "/about" ? "is-active" : ""}
            to="/about"
            onClick={toTop}
          >
            Our story
          </Link>
          <Link
            className={location.pathname === "/contact" ? "is-active" : ""}
            to="/contact"
            onClick={toTop}
          >
            Contact Us
          </Link>
        </nav>

        {searchOpen && (
          <div className="site-header__search" id="site-search">
            <form
              className="site-header__search-form"
              onSubmit={handleSearch}
              role="search"
            >
              <label htmlFor="header-search">What are you looking for?</label>
              <div className="site-header__search-field">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="10.8" cy="10.8" r="6.8" />
                  <path d="m16 16 4.3 4.3" />
                </svg>
                <input
                  ref={searchInputRef}
                  id="header-search"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search products"
                  autoComplete="off"
                />
                <button type="submit">Search</button>
              </div>
            </form>
            <button
              className="site-header__search-close"
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="m5 5 10 10M15 5 5 15" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {menuOpen && (
        <div className="site-header__mobile-layer">
          <button
            className="site-header__drawer-backdrop"
            type="button"
            onClick={() => setMenuOpen(false)}
            tabIndex="-1"
            aria-hidden="true"
          />
          <aside
            ref={menuDrawerRef}
            className="site-header__drawer"
            id="mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="site-header__drawer-top">
              <span className="site-header__drawer-title">Menu</span>
              <button
                ref={menuCloseRef}
                className="site-header__drawer-close"
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="m5 5 10 10M15 5 5 15" />
                </svg>
              </button>
            </div>

            <nav
              className="site-header__mobile-nav"
              aria-label="Mobile main navigation"
            >
              <Link to="/shop" onClick={toTop}>
                <span>Shop all</span>
                <span aria-hidden="true">↗</span>
              </Link>
              {categoryItems.map((category) => (
                <Link
                  key={category.value}
                  to={`/shop?category=${encodeURIComponent(category.value)}`}
                  onClick={toTop}
                >
                  <span>{category.label}</span>
                  <span aria-hidden="true">→</span>
                </Link>
              ))}
            </nav>

            <div className="site-header__drawer-links">
              <Link to="/about" onClick={toTop}>
                Our story
              </Link>
              <Link to="/contact" onClick={toTop}>
                Contact
              </Link>
              <Link to="/shipping" onClick={toTop}>
                Shipping
              </Link>
              <Link to="/policies" onClick={toTop}>
                Policies &amp; legal
              </Link>
              <Link to="/privacy" onClick={toTop}>
                Privacy policy
              </Link>
              <Link to="/cookies" onClick={toTop}>
                Cookie policy
              </Link>
              <Link to="/payments" onClick={toTop}>
                Payments &amp; pricing
              </Link>
              <Link to="/terms" onClick={toTop}>
                Terms of service
              </Link>
              <Link to="/accessibility" onClick={toTop}>
                Accessibility
              </Link>
              <Link to="/faqs" onClick={toTop}>
                FAQs
              </Link>
            </div>

            <div className="site-header__drawer-footer">
              <Link to="/shop?view=wishlist" onClick={toTop}>
                Saved ({wishlistCount})
              </Link>
              <Link to="/cartpage" onClick={toTop}>
                Bag ({cartCount})
              </Link>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}

export default Header;
